import { useInstance } from '@meraki-internal/react-dependency-injection';
import { FC, useState } from 'react';
import { DeviceInfo } from '../support/DeviceInfo';
import { ContactPermissionsModal } from './ContactPermissionsModal';
import { IonButton } from '@ionic/react';
import { isPlatform } from '../utils/isPlatform';
import { DuoAddressCardIcon } from '../theme/Icons';
import { Contacts } from '@capacitor-community/contacts';
import { AlertPresenter } from '../AlertBinder';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import { IContactMethod } from '../caremap/ICareMap';

export type IAddFromContactsButtonResult = {
    firstName?: string;
    lastName?: string;
    address?: string;
    organization?: string;
    contactMethods: IContactMethod[];
};

export type IAddFromContactsButtonProps = {
    btnRef?: React.RefObject<HTMLIonButtonElement>;
    onContactSelected: (patch: IAddFromContactsButtonResult) => void;
};

export const AddFromContactsButton: FC<IAddFromContactsButtonProps> = ({ onContactSelected, btnRef }) => {
    const alertPresenter = useInstance(AlertPresenter);
    const { platform, iOSVersion } = useInstance(DeviceInfo);

    const [openContactsModal, setOpenContactsModal] = useState<boolean>(false);

    if (!isPlatform('capacitor')) {
        return;
    }

    const stripLeading1 = (phone: string) => {
        const digits = phone.replace(/[^0-9x]/g, '');
        return (digits[0] === '1' ? digits.substring(1) : digits);
    };

    const isUSPhone = (phone: string) => {
        const beginning = phone.split('x')[0]; // if has extension
        const digits = beginning.replace(/[^0-9]/g, '');
        return (digits.length === 10 || (digits.length === 11 && digits[0] === '1'));
    };

    const onAddClicked = async () => {
        console.log('clicked');
        const permissionState = await Contacts.checkPermissions();

        if (platform === 'ios' && String(iOSVersion).startsWith('18') && permissionState.contacts === 'prompt') {
            setOpenContactsModal(true);
        } else {
            selectContact();
        }
    };

    const selectContact = async () => {
        // ask the user for permission
        let permissionState = await Contacts.requestPermissions();

        if (permissionState.contacts !== 'granted') {
            let subHeader = '';
            let message = '';
            if (isPlatform('ios')) {
                subHeader = 'You need to allow full access to your Contacts to use them in Innerhive.';
                message = 'Tap "Open Settings" below, then select Contacts and choose "Full Access".';
            } else if (isPlatform('android')) {
                subHeader = 'You need to allow access to your Contacts to use them in Innerhive.';
                message = 'Tap "Open Settings" below, then select Permissions > Contacts and choose "Allow".';
            }
            const action = await alertPresenter.showAlertV2({
                header: 'Access to Contacts Disabled',
                subHeader: subHeader,
                message: message,
                backdropDismiss: true,
                options: [{
                    id: 'cancel',
                    label: 'No Thanks',
                    role: 'cancel'
                }, {
                    id: 'continue',
                    label: 'Open Settings'
                }]
            });

            if (action === 'continue') {
                NativeSettings.open({ optionIOS: IOSSettings.App, optionAndroid: AndroidSettings.ApplicationDetails });
            }
        }

        permissionState = await Contacts.checkPermissions();

        if (permissionState.contacts !== 'granted') {
            return;
        }

        const { contact } = await Contacts.pickContact({
            projection: {
                name: true,
                phones: true,
                emails: true,
                organization: true,
                postalAddresses: true
            }
        });

        // the above call never resolves if the user aborts
        // so if we get to here, we have a contact

        const addrs = contact.postalAddresses || [];
        const address = addrs.find(a => a.isPrimary) || addrs.find(a => a.type === 'home') || addrs?.[0];

        const phones = (contact.phones || []).filter(p => p.number && isUSPhone(p.number) && !p.type.includes('fax') && !p.type.includes('pager')) || [];
        const workPhone = phones.find(p => p.type.includes('work'));
        const otherPhones = phones.filter(p => p !== workPhone);
        const mobilePhone = otherPhones.find(p => p.type === 'mobile') || otherPhones.find(p => p.isPrimary) || otherPhones?.[0];

        const emails = (contact.emails || []).filter(e => Boolean(e.address)) || [];
        const email = emails.find(e => e.isPrimary) || emails?.[0];

        const patch: IAddFromContactsButtonResult = {
            firstName: contact.name?.given || '',
            lastName: contact.name?.family || '',
            address: address ? (address as any).formatted.replace(/\n/g, ', ') : '',
            organization: contact.organization?.company || '',
            contactMethods: [
                { type: 'email', value: email?.address || '' },
                { type: 'mobile-phone', value: mobilePhone ? stripLeading1(mobilePhone.number!) : '' },
                { type: 'work-phone', value: workPhone ? stripLeading1(workPhone.number!) : '' }
            ]
        };

        onContactSelected(patch);
    };

    return (
        <>
            <ContactPermissionsModal
                isOpen={openContactsModal}
                onClose={() => setOpenContactsModal(false)}
                onButtonClick={() => {
                    setOpenContactsModal(false);
                    selectContact();
                }}
            />
            <div style={{marginTop: -15, marginBottom: 22}}>
                <IonButton ref={btnRef} fill="outline" onClick={onAddClicked}>
                    <DuoAddressCardIcon slot="start" />&nbsp;&nbsp;Add From Contacts
                </IonButton>
            </div>
        </>
    );
};
